/**
 * Pure helpers for run-standup commit mapping + derived intent (design §4.4 / §5.3).
 * No React — safe to unit-test without a DOM runner.
 */

import type { Focus, RunIssue } from "../../../lib/api";

export type DerivedIntent = "active" | "parked" | "blocked";

/**
 * ≥1 tick or non-empty typed → active (typed wins over blocked-only ticks).
 * Empty → parked.
 * Every ticked issue is Jira-blocked and typed empty → blocked.
 */
export function deriveIntent(
  ticked: { blocked: boolean }[],
  typedLine: string
): DerivedIntent {
  const typed = typedLine.trim();
  if (ticked.length === 0 && !typed) return "parked";
  if (typed) return "active";
  if (ticked.length > 0 && ticked.every((i) => i.blocked)) return "blocked";
  return "active";
}

export type StandupActivityWrite = {
  projectId: string;
  nzDate: string;
  type: "standup";
  actor: string;
  text: string;
  sourceRef?: string;
};

function newFocusId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `f-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  }
}

/** Map one project lane's ticks + typed line → Focus[] + Activity writes (§4.4). */
export function commitmentsForProject(input: {
  projectId: string;
  ticked: RunIssue[];
  typedLine: string;
  actor: string;
  boardDate: string;
  idFn?: () => string;
}): { focuses: Focus[]; activities: StandupActivityWrite[] } {
  const idFn = input.idFn ?? newFocusId;
  const focuses: Focus[] = [];
  const activities: StandupActivityWrite[] = [];
  const projectId = input.projectId.trim();

  for (const issue of input.ticked) {
    focuses.push({
      id: idFn(),
      text: issue.summary,
      done: false,
      projectId,
      issueKey: issue.key,
    });
    activities.push({
      projectId,
      nzDate: input.boardDate,
      type: "standup",
      actor: input.actor,
      text: issue.summary,
      sourceRef: issue.key,
    });
  }

  const typed = input.typedLine.trim();
  if (typed) {
    focuses.push({ id: idFn(), text: typed, done: false, projectId });
    activities.push({
      projectId,
      nzDate: input.boardDate,
      type: "standup",
      actor: input.actor,
      text: typed,
    });
  }

  return { focuses, activities };
}

/** Unassigned lane: Focus only, no Activity (no projectId). */
export function commitmentsForUnassigned(input: {
  typedLine: string;
  ticked?: { summary: string; key?: string }[];
  idFn?: () => string;
}): { focuses: Focus[] } {
  const idFn = input.idFn ?? newFocusId;
  const focuses: Focus[] = [];
  for (const issue of input.ticked ?? []) {
    focuses.push({
      id: idFn(),
      text: issue.summary,
      done: false,
      ...(issue.key ? { issueKey: issue.key } : {}),
    });
  }
  const typed = input.typedLine.trim();
  if (typed) focuses.push({ id: idFn(), text: typed, done: false });
  return { focuses };
}
